import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, RotateCcw, X } from "lucide-react";

const STATUS_LABEL = {
  alocado: { label: "Alocado", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  parcial: { label: "Parcial", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  devolvido: { label: "Devolvido", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  fechado: { label: "Fechado", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

export default function EventStockCard({ eventId, confirmedPromoters = [], canEdit }) {
  const [allocs, setAllocs] = useState([]);
  const [items, setItems] = useState([]);
  const [itemId, setItemId] = useState("");
  const [promoterId, setPromoterId] = useState("");
  const [qty, setQty] = useState(1);
  const [ret, setRet] = useState(null); // allocation being reconciled

  const load = useCallback(() => {
    api.get(`/events/${eventId}/stock`).then((r) => setAllocs(r.data || [])).catch(() => setAllocs([]));
  }, [eventId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/stock/items").then((r) => setItems(r.data || [])).catch(() => setItems([])); }, []);

  const allocate = async () => {
    if (!itemId) { toast.error("Escolhe um artigo"); return; }
    try {
      await api.post(`/events/${eventId}/stock`, { item_id: itemId, promoter_id: promoterId || null, qty: parseInt(qty) || 1 });
      toast.success("Artigo alocado");
      setQty(1); setPromoterId("");
      load();
    } catch (e) { toast.error("Erro ao alocar", { description: e?.response?.data?.detail || e.message }); }
  };

  const removeAlloc = async (a) => {
    if (!window.confirm("Remover esta alocação?")) return;
    try { await api.delete(`/stock/allocations/${a.id}`); toast.success("Alocação removida"); load(); }
    catch (e) { toast.error("Erro", { description: e?.response?.data?.detail || e.message }); }
  };

  const submitReturn = async () => {
    const a = ret;
    const returned = parseInt(ret._returned) || 0;
    const lost = parseInt(ret._lost) || 0;
    try {
      await api.post(`/stock/allocations/${a.id}/return`, { qty_returned: returned, lost_qty: lost, lost_reason: ret._reason || null });
      toast.success("Devolução registada");
      setRet(null); load();
    } catch (e) { toast.error("Erro na devolução", { description: e?.response?.data?.detail || e.message }); }
  };

  const selItem = items.find((i) => i.id === itemId);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="event-stock-card">
      <div className="flex items-center gap-2 mb-1"><Boxes className="w-5 h-5 text-zinc-700" /><h3 className="text-base font-display font-bold text-zinc-950">Fardamento & Equipamento</h3></div>
      <p className="text-sm text-zinc-500 mb-4">Atribui artigos ao evento (opcionalmente a um promotor) e regista devoluções.</p>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-2 mb-4 bg-zinc-50/60 border border-zinc-100 rounded-lg p-3">
          <div className="flex-1 min-w-[180px]">
            <label className="text-xs font-medium text-zinc-500">Artigo</label>
            <select value={itemId} onChange={(e) => setItemId(e.target.value)} data-testid="stock-alloc-item" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm">
              <option value="">Escolher…</option>
              {items.map((i) => <option key={i.id} value={i.id} disabled={i.available_qty <= 0}>{i.name}{i.variant ? ` ${i.variant}` : ""} — disp. {i.available_qty}</option>)}
            </select>
          </div>
          <div className="min-w-[160px]">
            <label className="text-xs font-medium text-zinc-500">Promotor (opcional)</label>
            <select value={promoterId} onChange={(e) => setPromoterId(e.target.value)} data-testid="stock-alloc-promoter" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm">
              <option value="">— sem promotor —</option>
              {confirmedPromoters.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="w-20">
            <label className="text-xs font-medium text-zinc-500">Qtd</label>
            <input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="stock-alloc-qty" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
          </div>
          <button onClick={allocate} data-testid="stock-alloc-add" className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-sm font-semibold">
            <Plus className="w-4 h-4" /> Alocar
          </button>
        </div>
      )}
      {selItem && <div className="text-xs text-zinc-500 -mt-2 mb-3">Disponível de {selItem.name}{selItem.variant ? ` ${selItem.variant}` : ""}: <strong>{selItem.available_qty}</strong></div>}

      <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100">
        {allocs.length === 0 && <div className="px-4 py-6 text-center text-xs text-zinc-400">Sem artigos alocados a este evento.</div>}
        {allocs.map((a) => {
          const st = STATUS_LABEL[a.status] || STATUS_LABEL.alocado;
          const consumable = a.type === "consumivel";
          const canReturn = !consumable && (a.status === "alocado" || a.status === "parcial");
          return (
            <div key={a.id} data-testid={`event-alloc-${a.id}`} className="px-4 py-2.5 flex items-center gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-zinc-900">{a.item_name}{a.variant ? ` ${a.variant}` : ""} <span className="text-zinc-400 font-normal">× {a.qty_allocated}</span></div>
                <div className="text-xs text-zinc-500">{a.promoter_name ? a.promoter_name : "sem promotor"}{!consumable && (a.qty_returned || a.lost_qty) ? ` · devolvido ${a.qty_returned}${a.lost_qty ? `, em falta ${a.lost_qty} (${a.lost_reason || ""})` : ""}` : ""}</div>
              </div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${consumable ? "bg-orange-50 text-orange-700 border-orange-200" : st.cls}`}>{consumable ? "Consumível" : st.label}</span>
              {canEdit && canReturn && (
                <button onClick={() => setRet({ ...a, _returned: a.qty_allocated - a.lost_qty, _lost: a.lost_qty || 0, _reason: a.lost_reason || "" })} data-testid={`stock-return-${a.id}`} className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded border border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <RotateCcw className="w-3 h-3" /> Devolver
                </button>
              )}
              {canEdit && (
                <button onClick={() => removeAlloc(a)} data-testid={`stock-alloc-del-${a.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          );
        })}
      </div>

      {ret && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setRet(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="stock-return-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
              <h3 className="text-base font-display font-bold">Registar devolução</h3>
              <button onClick={() => setRet(null)} className="text-zinc-400 hover:text-zinc-900"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="text-sm text-zinc-600">{ret.item_name}{ret.variant ? ` ${ret.variant}` : ""} · alocado <strong>{ret.qty_allocated}</strong>{ret.promoter_name ? ` · ${ret.promoter_name}` : ""}</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-zinc-500">Devolvidos</label>
                  <input type="number" min="0" max={ret.qty_allocated} value={ret._returned} onChange={(e) => setRet({ ...ret, _returned: e.target.value })} data-testid="return-qty" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-500">Em falta</label>
                  <input type="number" min="0" max={ret.qty_allocated} value={ret._lost} onChange={(e) => setRet({ ...ret, _lost: e.target.value })} data-testid="return-lost" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm" />
                </div>
              </div>
              {parseInt(ret._lost) > 0 && (
                <div>
                  <label className="text-xs font-medium text-zinc-500">Motivo do que falta</label>
                  <select value={ret._reason} onChange={(e) => setRet({ ...ret, _reason: e.target.value })} data-testid="return-reason" className="w-full mt-1 px-3 py-2 rounded-lg border border-zinc-200 text-sm">
                    <option value="">Escolher…</option>
                    <option value="perdido">Perdido</option>
                    <option value="danificado">Danificado</option>
                    <option value="por_devolver">Por devolver</option>
                  </select>
                  <p className="text-[11px] text-zinc-400 mt-1">Perdido/danificado dá baixa ao stock total.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-zinc-100">
              <button onClick={() => setRet(null)} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm">Cancelar</button>
              <button onClick={submitReturn} data-testid="save-return" className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
